/* =====================================================================
   AstaLab 2.0 — livePollingController.js (round alpha.6 P0.2-A)

   Implementa fedelmente ALPHA6_P0_2_LIVE_POLLING_DESIGN.md (correzioni A-P, approvato), esteso da
   A7-P1-3A (purchase-ledger), A7-P1-5 (bid-history) e A7-P1-6/D2/D3 (activity-feed/nomination-ledger,
   con retry a revision invariata quando lo stato di attività è "stale"). Controller di polling
   DOM-free per la sessione live di un'asta: legge/scrive solo `document.visibilityState` (mai
   `container`/`querySelector`/`innerHTML`), non importa mai `AuctionDetailPage.js` né alcuna funzione
   di rendering — chiama esclusivamente `repository.loadLiveSession`/`repository.listAuctionPurchases`/
   `repository.listAuctionNominations`/`repository.listBidsForNomination` e notifica il chiamante
   tramite tre callback distinti (`onNonStructuralSnapshot`/`onStructuralTransition`/`onError`, §9-§10).

   Il controller non conosce mai `"closed"` come caso speciale (§10: "se non viene mai istanziato, non
   deve gestirlo") — resta responsabilità esclusiva del chiamante non invocare `start()` quando
   `lifecycle === "closed"` (CONTROLLER_MOUNTED, §5-bis/CORREZIONE F).

   Nessuna nuova RPC, nessuna nuova migration, nessuna dipendenza da Realtime — solo `setTimeout` +
   le letture già esistenti tramite il repository (§2, §11 del design; A7-P1-5/A7-P1-6 per le due
   aggiunte più recenti).
   ===================================================================== */

/** Cadence per stato — §7. Unico punto di configurazione: mai un valore letterale duplicato altrove
 *  in questo modulo. Nessuna entry per `"closed"`: il controller non viene mai montato in quello
 *  stato (responsabilità del chiamante, non di questo modulo — §10). */
export const POLL_CADENCE_MS = {
  setup: 10000,
  paused: 10000,
  running: 3000
};

/** Backoff minimale sui soli fallimenti CONSECUTIVI — §9. Formula: min(base * 2^failures, 4 * base).
 *  Azzerato al primo successo, mai un "circuit breaker" permanente. */
const MAX_BACKOFF_MULTIPLIER = 4;

/** CORREZIONE M/§4-quater: unico predicato di eleggibilità-azione realmente dinamico rispetto al
 *  poll, derivato dall'audit di `AuctionDetailPage.js:724`
 *  (`canBid = mySeat && highestBidderSeatId !== mySeat.id`). Mai un riferimento a `seats`/DOM qui —
 *  solo lo scalare `viewerContext.mySeatId`. */
export function viewerActionMode(snapshot, viewerContext){
  const mySeatId = viewerContext ? viewerContext.mySeatId : null;
  if(mySeatId == null) return "spectator";
  if(snapshot.highestBidderSeatId === mySeatId) return "highest-bidder";
  return "can-bid";
}

/** Numero di righe del ledger acquisti — §4-quinquies (A7-P1-3A). auction_purchases è
 *  STRUTTURALMENTE append-only nel percorso applicativo normale: settle_nomination() esegue SEMPRE e
 *  SOLO un INSERT (mai un UPDATE) su questa tabella — verificato direttamente nel testo di
 *  backend/migrations/0015_live_auction_settlement_budget_roster.sql, dove l'unica altra istruzione
 *  che tocca auction_purchases è un DELETE dentro admin_purge_synthetic_live_auction_test_data(), una
 *  utility di test/pulizia mai raggiungibile dal ciclo applicativo nominate/bid/finalize/settle.
 *  Nessuna RPC del percorso normale modifica MAI una riga già inserita. Di conseguenza, sotto
 *  funzionamento normale, LA LUNGHEZZA dell'array è un discriminante NECESSARIO E SUFFICIENTE per "il
 *  ledger è cambiato in un modo rilevante per la UI": non può mai esistere uno scenario in cui il
 *  conteggio resta invariato ma il contenuto di una riga esistente è cambiato (nessun percorso di
 *  scrittura lo permette), e ogni singolo nuovo acquisto incrementa il conteggio di esattamente 1. Un
 *  fingerprint più costoso (hash/JSON del contenuto) sarebbe quindi sempre equivalente a questo, mai
 *  più corretto — solo più caro da calcolare a ogni tick. */
function purchaseCountOf(snapshot){
  return snapshot.purchases ? snapshot.purchases.length : 0;
}

/** Numero di righe del ledger nomine — round A7-P1-6/D2/D3. auction_nominations è append-once per
 *  ogni nomina (nomination_seq = coalesce(max(nomination_seq),0)+1, mai ricalcolato/decrementato,
 *  verificato staticamente su 0014/0015/0016) — una riga esistente viene poi AGGIORNATA in place
 *  (status/final_amount/closed_at/...) ma non nasce mai una seconda volta e non viene mai rimossa
 *  dal ciclo applicativo normale. La LUNGHEZZA dell'array è quindi un discriminante sufficiente per
 *  "è stata creata una nuova nomina", esattamente come purchaseCountOf sopra lo è per "un acquisto è
 *  stato registrato" — ma NON è un discriminante per aggiornamenti di stato su una nomina già nota
 *  (open->pending_settlement/unsold/settled): quel caso resta coperto dai termini esistenti
 *  (nominationStatus/currentNominationId) quando l'osservatore vede lo stato intermedio, o dal solo
 *  fatto che la nomina è NUOVA quando non lo vede (round D2, "rapid remote unsold"). */
function nominationCountOf(snapshot){
  return snapshot.nominations ? snapshot.nominations.length : 0;
}

/** CORREZIONE C, estesa da CORREZIONE M — §4-bis/§4-quater. CORREZIONE round A7-P1-3A (§1-§4): la
 *  giustificazione storica per escludere `purchases` da questo confronto (vedi il vecchio commento su
 *  SNAPSHOT_FIELDS sotto, corretto in questo stesso round) confondeva due funzioni DIVERSE — è vero
 *  che un cambio di `purchases` implica sempre un cambio di `revision` PRIMA (motivo per cui
 *  escluderlo da `snapshotsEqual()`, che quel campo lo confronta già, resta corretto), ma
 *  `isStructural()` non ha MAI confrontato `revision` in primo luogo: i suoi quattro termini
 *  originali (lifecycle/currentNominationId/nominationStatus/viewerActionMode) potevano tutti
 *  restare INVARIATI netti — es. un ciclo nomina->settlement interamente completato da un altro
 *  partecipante fra due tick di poll di QUESTO osservatore, dove nominationStatus/currentNominationId
 *  tornano entrambi a null esattamente come prima — mentre auction_purchases aveva realmente
 *  guadagnato una riga. Un osservatore in quella condizione avrebbe applicato un patch NON
 *  strutturale (o nessun cambiamento affatto), non aggiornando mai budget/rosa/opponent intelligence
 *  per un acquisto realmente avvenuto — difetto reale multi-client, mai un artefatto di soli test
 *  (A7-P1-3A §1). Quinto termine additivo, mai in sostituzione dei quattro esistenti: un cambio del
 *  conteggio ledger (§ sopra, purchaseCountOf) è SEMPRE trattato come strutturale, indipendentemente
 *  da cosa dicano gli altri quattro termini. Nessuna nuova lettura di rete: `purchases` è già
 *  presente in ENTRAMBI gli snapshot per costruzione (già recuperato da tick() più sotto ad ogni
 *  cambio di revision, §4-ter/§2 — mai un fetch aggiuntivo introdotto da questa correzione). */
export function isStructural(prev, next, viewerContext){
  return prev.lifecycle !== next.lifecycle
    || prev.currentNominationId !== next.currentNominationId
    || prev.nominationStatus !== next.nominationStatus
    || viewerActionMode(prev, viewerContext) !== viewerActionMode(next, viewerContext)
    || purchaseCountOf(prev) !== purchaseCountOf(next)
    || nominationCountOf(prev) !== nominationCountOf(next);
}

/** Campi scalari del fingerprint — §8 punto 1. `purchases` resta intenzionalmente ESCLUSO da QUESTO
 *  confronto (mai da isStructural(), corretto sopra in A7-P1-3A): per costruzione (§2) un cambio di
 *  `purchases` richiede sempre un cambio di `revision` prima, e `revision` è già uno dei campi qui
 *  sotto — confrontare anche `purchases` qui sarebbe quindi sempre ridondante rispetto a `revision`
 *  per la sola domanda "è cambiato QUALCOSA" (mai una fonte di falso "invariato" per snapshotsEqual
 *  stesso — la correzione A7-P1-3A riguardava esclusivamente isStructural(), una funzione diversa con
 *  un insieme di termini diverso e mai contenente `revision`). */
const SNAPSHOT_FIELDS = [
  "lifecycle", "revision", "nominationStatus", "currentNominationId",
  "nominatedPlayerFantacalcioId", "nominatingSeatId",
  "currentHighestBid", "highestBidderSeatId", "bidSeq", "deadlineAt"
];

function snapshotsEqual(a, b){
  for(const field of SNAPSHOT_FIELDS){
    if(a[field] !== b[field]) return false;
  }
  return true;
}

/** Round alpha.6 P0.2-A1 (CORREZIONE T) — audit confermato in sola lettura su
 *  BackendAdapter.js/SupabaseBackendAdapter.js/ArtifactDemoBackendAdapter.js/AuctionRepository.js e
 *  sulla migration 0013 (`start_live_session`): `loadLiveSession(auctionId)` restituisce
 *  legittimamente `null` per un'asta valida finché `start_live_session()` non ha MAI inserito la riga
 *  in `auction_live_sessions` (0013_live_auction_foundation.sql:589, `insert into
 *  public.auction_live_sessions (...)`, eseguito SOLO al primo avvio riuscito) — `null` è quindi lo
 *  stato NORMALE, ripetuto a ogni tick, per qualunque asta ancora in `setup` mai avviata (mai un
 *  errore transitorio, mai un caso limite raro). Una volta inserita, quella riga non viene mai
 *  cancellata dal ciclo di vita normale (l'unico DELETE, riga 742 della stessa migration, è dentro
 *  `admin_purge_synthetic_live_auction_test_data`, un'utility di test/pulizia, mai una RPC applicativa
 *  raggiungibile dal ciclo start/pause/resume/close) — quindi `null` può ricorrere SOLO quando questo
 *  controller è montato con `lifecycle: "setup"` (mai per `paused`/`running`, dove la riga esiste per
 *  costruzione). Normalizzato qui a uno snapshot "setup" compatibile con SNAPSHOT_FIELDS — MAI
 *  propagato come eccezione/backoff (§9 è riservato ai fallimenti di RETE/RPC, non a uno stato di
 *  dominio valido). */
function normalizeLiveSessionSnapshot(raw){
  if(raw) return raw;
  return {
    lifecycle: "setup", revision: 0, nominationStatus: null, currentNominationId: null,
    nominatedPlayerFantacalcioId: null, nominatingSeatId: null,
    currentHighestBid: null, highestBidderSeatId: null, bidSeq: 0, deadlineAt: null
  };
}

/**
 * Avvia un controller di polling per la sessione live di un'asta. DOM-free per costruzione: legge/
 * scrive solo `document.visibilityState`/`document.addEventListener`, mai `container`/`document`
 * altrimenti.
 *
 * @param {Object} options
 * @param {string} options.auctionId
 * @param {Object} options.repository - usa solo loadLiveSession(auctionId)/
 *   listAuctionPurchases(auctionId)/listAuctionNominations(auctionId)/
 *   listBidsForNomination(auctionId, nominationId), già esistenti (§2, mai nuove RPC).
 * @param {Object} options.initialSnapshot - CORREZIONE G/§4-ter, OBBLIGATORIO — baseline reale del
 *   cold-render (mai un placeholder, mai lastAppliedSnapshot = null). Contiene tutti i campi di
 *   SNAPSHOT_FIELDS più `purchases`/`nominations` (array, [] se non caricate al cold-render — round
 *   A7-P1-6 §4/§7: `nominations` deve SEMPRE essere seminata dal conteggio reale già letto dal
 *   cold-render, mai zero, altrimenti il primo tick con nomine storiche preesistenti produrrebbe un
 *   falso positivo strutturale).
 * @param {"setup"|"paused"|"running"} options.lifecycle - stato al momento del mount (mai "closed":
 *   il chiamante non deve invocare start() in quel caso — CORREZIONE F/§5-bis).
 * @param {{mySeatId:string|null}} options.viewerContext - CORREZIONE M/§4-quater.
 * @param {(snapshot:Object)=>void} options.onNonStructuralSnapshot - snapshot include `purchases`/
 *   `nominations`/`activityError`/`activityRecovered` (round A7-P1-6, §9/§14 — la patch leggera del
 *   chiamante può così impostare/ripulire l'indicatore di errore del feed attività senza mai
 *   ricostruire il contenuto delle righe, di competenza esclusiva del render completo).
 * @param {(snapshot:Object)=>void} options.onStructuralTransition - round A7-P1-6 (§10): riceve ORA
 *   lo snapshot (mai più zero argomenti) — il chiamante rifà un full-render authoritative esistente,
 *   che rilegge tutto da sé di norma, ma può riusare `snapshot.nominations` come prefetch SOLO
 *   quando `snapshot.nominationsFetchedFreshThisTick` è vero (§11/§12 — mai una nomination array
 *   stale spacciata per fresca in un render innescato da un ALTRO termine strutturale).
 * @param {(error:Error)=>void} options.onError
 * @param {boolean} [options.initialActivityStale] - round A7-P1-6F (fix del double-failure
 *   handoff, A7-P1-6V) — di norma assente/false per il PRIMO controller di una sessione pagina
 *   (cold-render riuscito). Il chiamante lo passa true quando sta sostituendo un controller
 *   precedente il cui `getActivityHandoff()` riportava `activityStale:true` (o quando il proprio
 *   tentativo di lettura nomine, cold-render o fallback di un render strutturale, è appena fallito)
 *   — senza questo, un doppio fallimento (poll + fallback del render) perderebbe per sempre lo
 *   stato "da ritentare", bloccando ogni recupero fino a una mutazione remota futura mai garantita.
 * @param {boolean} [options.initialActivityContentUnknown] - round A7-P1-6F — true quando il
 *   contenuto che il chiamante sta per mostrare/ha appena mostrato NON riflette una lettura nomine
 *   riuscita (stessa condizione di initialActivityStale sopra, tipicamente lo stesso valore). Forza
 *   il PROSSIMO fetch riuscito a produrre sempre un render completo, anche se il conteggio nomine
 *   risultante coincide col valore preesistente — quel valore preesistente non è mai stato
 *   realmente mostrato con successo dopo un doppio fallimento (§5/§9 del round F).
 * @returns {{stop:()=>void, invalidateForMutation:()=>void, getActivityHandoff:()=>{nominations:Array, activityStale:boolean, activityContentUnknown:boolean}}}
 *   `getActivityHandoff()` — round A7-P1-6F: il chiamante lo interroga SEMPRE subito prima di
 *   stop(), per seminare correttamente un controller successivo (initialSnapshot.nominations +
 *   initialActivityStale/initialActivityContentUnknown sopra) senza mai fabbricare una baseline
 *   vuota quando una reale già esiste.
 */
export function start(options){
  const {
    auctionId, repository, initialSnapshot, lifecycle, viewerContext,
    onNonStructuralSnapshot, onStructuralTransition, onError,
    initialActivityStale, initialActivityContentUnknown
  } = options;

  const baseCadenceMs = POLL_CADENCE_MS[lifecycle];

  let stopped = false;
  let localEpoch = 0;
  let lastAppliedSnapshot = initialSnapshot;   // CORREZIONE G — mai null
  let pendingTimer = null;                     // CORREZIONE N — riferimento all'UNICO setTimeout pendente, se esiste
  let requestInFlight = false;                 // CORREZIONE N — protegge il trigger esterno visibilitychange
  let consecutiveFailures = 0;                 // §9 — backoff sui soli fallimenti consecutivi
  // Round A7-P1-6/D3 (§6-§7) — persistente FRA i tick (mai per-tick): true dal momento in cui una
  // lettura di listAuctionNominations() fallisce, fino al successo della PROSSIMA lettura, qualunque
  // sia la sua causa scatenante. Guida shouldReadNominations più sotto, indipendentemente da
  // snapshot.revision — un fallimento a revision R+2 senza alcuna ulteriore mutazione remota deve
  // comunque essere ritentato a ogni tick successivo, mai abbandonato in attesa di un cambio di
  // revision che potrebbe non arrivare mai più.
  let activityStale = initialActivityStale === true;
  // Round A7-P1-6F (fix del "double-failure handoff" — A7-P1-6V) — SEPARATO da `activityStale`
  // sopra, mai la stessa variabile: `activityStale` governa SOLO se ritentare il fetch a revision
  // invariata; questo flag governa se il CONTENUTO attualmente mostrato dal render è noto per essere
  // corretto. Un fallimento del solo poll (senza mai un render nel frattempo, §A7-P1-6,3) lascia
  // questo flag falso — il contenuto già mostrato resta valido, la patch leggera basta. Seminato true
  // SOLO da chi crea questo controller quando il PROPRIO tentativo di lettura nomine (cold-render o
  // fallback di un render strutturale) è a sua volta fallito — in quel caso il contenuto mostrato
  // ORA non riflette mai una lettura riuscita, quindi un successivo fetch riuscito deve SEMPRE
  // forzare un render completo (mai una patch leggera) per popolare davvero il contenuto reale,
  // indipendentemente dal fatto che il conteggio nomine risultante coincida o non coincida col
  // valore preesistente (§5/§9 del round F: un conteggio N->N dopo un doppio fallimento non è mai
  // stato mostrato correttamente, quindi il recupero resta comunque strutturale). Azzerato SOLO
  // quando un fetch di QUESTO tick è realmente riuscito (nominationsFetchedFreshThisTick), mai da un
  // semplice passare del tempo o da un termine strutturale non correlato.
  let activityContentUnknown = initialActivityContentUnknown === true;

  function currentCadenceMs(){
    const multiplier = Math.min(Math.pow(2, consecutiveFailures), MAX_BACKOFF_MULTIPLIER);
    return baseCadenceMs * multiplier;
  }

  // CORREZIONE N — ogni chiamata a scheduleTick() è sempre preceduta, nel resto di questo modulo, da
  // una garanzia esplicita che pendingTimer sia già null: mai una sovrascrittura di un timer valido.
  function scheduleTick(delayMs){
    pendingTimer = setTimeout(tick, delayMs);
  }

  function applyIfChanged(nextSnapshot){
    // Round A7-P1-6/D3 (§7/§9) — correzione critica: snapshotsEqual() confronta SOLO
    // SNAPSHOT_FIELDS, che non contiene mai `nominations` (per lo stesso motivo per cui non contiene
    // `purchases` — §8 punto 1 storico). Per `purchases` questo è sempre stato sicuro perché un suo
    // cambio richiede SEMPRE un cambio di revision prima (revision È in SNAPSHOT_FIELDS). Per
    // `nominations` questo NON è più garantito dopo l'introduzione del retry a revision invariata
    // (§6): un recovery riuscito dopo un fallimento precedente può scoprire un conteggio nomine
    // cambiato mentre `revision` è rimasta iDENTICA a quella già applicata (bloccata dal tick
    // fallito precedente). Il confronto diretto qui sotto, mai delegato a snapshotsEqual/
    // SNAPSHOT_FIELDS, chiude esattamente questo divario. `activityRecovered` (§9/§14) è una SECONDA
    // ragione indipendente per non uscire in anticipo: un recupero "silenzioso" (stesso conteggio,
    // nessun altro campo cambiato) deve comunque raggiungere la patch leggera per ripulire
    // l'indicatore di errore/stale già scritto nel DOM da un tick precedente.
    const nominationsCountUnchanged = nominationCountOf(lastAppliedSnapshot) === nominationCountOf(nextSnapshot);
    if(snapshotsEqual(lastAppliedSnapshot, nextSnapshot) && nominationsCountUnchanged && !nextSnapshot.activityRecovered){
      // §8 punto 1 — "unchanged snapshot does not cause destructive rerender": nessun callback
      // invocato, zero tocchi al DOM.
      return;
    }
    // Round A7-P1-6F — termine additivo, MAI in sostituzione dei sei esistenti: un fetch nomine
    // riuscito in QUESTO tick mentre il contenuto mostrato era noto per essere sconosciuto/non
    // corretto (activityContentUnknown, sopra) forza SEMPRE un render completo, anche quando il
    // conteggio nomine risultante coincide col valore preesistente — quel valore preesistente non è
    // mai stato realmente mostrato con successo, quindi una patch leggera (che non ricostruisce mai
    // il contenuto, §13 del design D3) lascerebbe il DOM in uno stato scorretto/vuoto con
    // l'indicatore di errore ripulito (esattamente il difetto identificato in A7-P1-6V).
    const structural = isStructural(lastAppliedSnapshot, nextSnapshot, viewerContext)
      || (nextSnapshot.nominationsFetchedFreshThisTick && activityContentUnknown);
    // Azzerato non appena un fetch di QUESTO tick è realmente riuscito, indipendentemente dal motivo
    // per cui `structural` è risultato vero — un fetch fresco, per costruzione, rende il contenuto
    // di nuovo noto/corretto (mai un secondo fallimento silenzioso qui: se QUESTO fetch fosse
    // fallito, nominationsFetchedFreshThisTick sarebbe rimasto falso e questa riga non eseguirebbe).
    if(nextSnapshot.nominationsFetchedFreshThisTick) activityContentUnknown = false;
    lastAppliedSnapshot = nextSnapshot;
    if(structural){
      // Round A7-P1-6 (§10) — passa lo snapshot (mai più zero argomenti): il chiamante può così
      // riusare `nextSnapshot.nominations` come prefetch per il render completo SOLO quando
      // `nextSnapshot.nominationsFetchedFreshThisTick` è vero (§11/§12) — mai una nomination array
      // ormai stale spacciata per fresca in un render innescato da un ALTRO termine strutturale.
      onStructuralTransition(nextSnapshot);
    } else {
      onNonStructuralSnapshot(nextSnapshot);
    }
  }

  // Pseudocodice di riferimento: "Scheduler linearizzato — CORREZIONE N" + "stop() è terminale —
  // CORREZIONE H" (ricontrollo di `stopped` ad ogni ripresa asincrona) + lettura condizionale delle
  // purchases (§2) + backoff sui fallimenti consecutivi (§9), tutti combinati senza contraddirsi.
  async function tick(){
    pendingTimer = null;                          // (CORREZIONE L) questo timer ha già scattato, non è più "pendente"
    if(stopped) return;                            // (1) guardia iniziale — CORREZIONE H
    if(document.visibilityState === "hidden") return;   // difensivo: onVisibilityChange già cancella
                                                          //   pendingTimer su hidden — CORREZIONE N
    requestInFlight = true;                         // CORREZIONE N
    const requestEpoch = localEpoch;                // CORREZIONE B — catturato PRIMA di qualunque I/O
    try{
      const rawSnapshot = await repository.loadLiveSession(auctionId);   // UNICA lettura sempre eseguita — §2
      if(stopped) return;                           // (2) ricontrollo dopo l'await — CORREZIONE H
      const snapshot = normalizeLiveSessionSnapshot(rawSnapshot);   // CORREZIONE T — null è uno stato "setup" valido, mai un errore

      let purchases = lastAppliedSnapshot.purchases;
      let nominations = lastAppliedSnapshot.nominations;
      let bidHistory = lastAppliedSnapshot.bidHistory;
      let bidHistoryNominationId = lastAppliedSnapshot.bidHistoryNominationId;
      let bidHistoryError = lastAppliedSnapshot.bidHistoryError || false;
      // Round A7-P1-6 (§10/§12/§13/§14 del design) — segnali PER-TICK, mai persistenti: azzerati a
      // ogni chiamata di tick(), mai riusati da un giro all'altro (a differenza di `activityStale`,
      // dichiarata più sopra nella chiusura di start()).
      let activityError = false;               // questo tick ha tentato ed è FALLITO
      let activityRecovered = false;            // questo tick ha tentato, è riuscito, ed era stale PRIMA del tentativo
      let nominationsFetchedFreshThisTick = false;   // questo tick ha OTTENUTO una risposta fresca e riuscita

      if(requestEpoch === localEpoch && snapshot.revision !== lastAppliedSnapshot.revision){
        // Condizionale — §2: solo se questa richiesta non è già stale (evita una fetch inutile per
        // una risposta che verrà comunque scartata sotto) E la revision è cambiata (necessario per
        // costruzione perché auction_purchases cambi, §2).
        purchases = await repository.listAuctionPurchases(auctionId);
        if(stopped) return;                         // (2-bis) ricontrollo dopo il secondo await — CORREZIONE H
      }

      // Round A7-P1-6/D2/D3 (§6-§7) — shouldReadNominations è INDIPENDENTE dal solo cambio di
      // revision: un fallimento precedente lascia `activityStale=true`, che da solo basta a
      // ritentare ANCHE quando la revision è rimasta identica a quella già applicata (altrimenti un
      // fallimento senza alcuna ulteriore mutazione remota non verrebbe MAI più ritentato — round
      // D3 §6). Ricontrollato di nuovo qui (mai una condizione catturata prima dell'await sopra).
      if(requestEpoch === localEpoch && (snapshot.revision !== lastAppliedSnapshot.revision || activityStale)){
        const wasStaleBeforeThisAttempt = activityStale;
        try{
          const freshNominations = await repository.listAuctionNominations(auctionId);
          if(stopped) return;
          nominations = freshNominations;
          nominationsFetchedFreshThisTick = true;
          activityStale = false;
          activityRecovered = wasStaleBeforeThisAttempt;
        }catch(e){
          // Isolato nel proprio try/catch — mai propagato al catch esterno di questa funzione: non
          // deve MAI incrementare consecutiveFailures/attivare onError/il backoff (§13 del design),
          // né impedire l'applicazione del resto dello snapshot già ottenuto sopra/sotto. `nominations`
          // resta DELIBERATAMENTE al valore precedente (mai svuotato/azzerato, §7 — il confronto
          // futuro deve restare monotono contro l'ultima vista riuscita).
          activityStale = true;
          activityError = true;
        }
      }

      if(requestEpoch === localEpoch && snapshot.revision !== lastAppliedSnapshot.revision){
        // Round A7-P1-5 (§12/§13), estesa da A7-P1-6 (§16): l'history della nomina corrente si
        // arricchisce SOLO se questa transizione risulterà NON strutturale — ora isStructural()
        // include anche il termine nominationCountOf (sopra), quindi questa stessa chiamata pura
        // riflette già correttamente anche un cambio del ledger nomine, mai una doppia fonte di
        // verità. Una transizione strutturale rifà comunque un render completo da zero, che
        // recupera la history per conto proprio — mai un secondo fetch reale sprecato solo per
        // essere ignorato subito dopo.
        const willBeStructural = isStructural(lastAppliedSnapshot, { ...snapshot, purchases, nominations }, viewerContext);

        // Amendment 3/§11 (A7-P1-5) — arricchimento NON critico: un fallimento qui è isolato nel
        // proprio try/catch, mai propagato al catch esterno di questa funzione — non deve MAI
        // incrementare consecutiveFailures/attivare onError/il backoff, né impedire l'applicazione
        // del resto dello snapshot (bid corrente/miglior offerente/countdown) già ottenuto sopra.
        // Nessuna nomina corrente -> nessun fetch (§14, HISTORY_FETCH_WITHOUT_CURRENT_NOMINATION=NO)
        // — bidHistory/bidHistoryNominationId tornano esplicitamente a null, mai lasciati a un
        // valore ormai riferito a una nomina già chiusa.
        if(!willBeStructural && snapshot.nominationStatus && snapshot.currentNominationId != null){
          try{
            bidHistory = await repository.listBidsForNomination(auctionId, snapshot.currentNominationId);
            bidHistoryNominationId = snapshot.currentNominationId;
            bidHistoryError = false;
          }catch(e){
            // Sul fallimento, bidHistory/bidHistoryNominationId restano DELIBERATAMENTE al valore
            // precedente (mai svuotati) — l'ultima history già mostrata con successo resta valida a
            // schermo; solo bidHistoryError segnala il fallimento di QUESTO giro alla patch leggera.
            bidHistoryError = true;
          }
          if(stopped) return;
        } else if(!willBeStructural){
          bidHistory = null;
          bidHistoryNominationId = null;
          bidHistoryError = false;
        }
      }

      if(requestEpoch === localEpoch){
        // CORREZIONE B — una mutazione locale iniziata dopo l'emissione di questa richiesta invalida
        // l'epoch: la risposta, anche se arrivata mentre la RPC della mutazione è ancora in corso,
        // viene scartata incondizionatamente qui (ricontrollato di nuovo qui perché l'epoch può
        // essere avanzato anche durante l'await di listAuctionPurchases/listAuctionNominations/
        // listBidsForNomination sopra).
        applyIfChanged({
          ...snapshot, purchases, nominations, bidHistory, bidHistoryNominationId, bidHistoryError,
          activityError, activityRecovered, nominationsFetchedFreshThisTick
        });
      }
      consecutiveFailures = 0;                      // §9 — azzerato al primo successo
    }catch(e){
      if(stopped) return;                           // (2-ter) stesso ricontrollo sul ramo di errore — CORREZIONE H
      consecutiveFailures++;                         // §9
      onError(e);
    }finally{
      requestInFlight = false;                       // CORREZIONE N
      if(!stopped && document.visibilityState === "visible" && pendingTimer === null){
        scheduleTick(currentCadenceMs());             // (4) cancello finale — CORREZIONE H
      }
    }
  }

  function onVisibilityChange(){
    // CORREZIONE N — linearizzazione esplicita: mai una chiamata diretta a tick() da qui.
    if(stopped) return;
    if(document.visibilityState === "hidden"){
      if(pendingTimer !== null){ clearTimeout(pendingTimer); pendingTimer = null; }
      // Nessuna nuova request iniziata: una eventualmente già in volo (requestInFlight) può
      // concludersi da sola — il suo stesso finally, vedendo "hidden", non ri-schedula nulla.
    } else {
      if(requestInFlight){
        // Una request è già il refresh corrente: NON iniziarne una seconda — il suo finally
        // riprenderà il ciclo da solo (vede "visible" + pendingTimer===null quando risolve).
        return;
      }
      if(pendingTimer !== null){ clearTimeout(pendingTimer); pendingTimer = null; }   // residuo difensivo
      scheduleTick(0);   // UNICO tick immediato, mai una chiamata diretta a tick()
    }
  }

  document.addEventListener("visibilitychange", onVisibilityChange);

  // CORREZIONE L — start() non esegue MAI I/O sincrono: solo una schedulazione. start() ritorna
  // sempre sincronicamente.
  scheduleTick(0);

  function stop(){
    // CORREZIONE H — terminale: dopo stop(), nessun callback, nessun refresh di visibilità, nessun
    // nuovo timeout può mai essere schedulato per questa istanza.
    stopped = true;
    if(pendingTimer !== null){ clearTimeout(pendingTimer); pendingTimer = null; }   // cancella ANCHE il tick a 0ms iniziale — CORREZIONE L
    document.removeEventListener("visibilitychange", onVisibilityChange);
  }

  function invalidateForMutation(){
    // CORREZIONE B — sincrona, nessun I/O, nessuna richiesta avviata da qui. Chiamata dal chiamante
    // PRIMA di ogni await verso una delle otto mutazioni (start/pause/resume/close/nominate/bid/
    // finalize/settle).
    localEpoch++;
  }

  // Round A7-P1-6F — handoff esplicito dello stato-ledger nomine verso un EVENTUALE controller
  // successivo (il chiamante lo interroga SEMPRE subito prima di stop(), mai dopo — vedi
  // disposeActiveAuctionDetailResources() in AuctionDetailPage.js). Sincrona, nessun I/O, legge solo
  // chiusure già esistenti — mai un nuovo stato introdotto SOLO per questo metodo.
  // `nominations`: l'ultimo array realmente noto a questo controller (mai forzato a [] qui — se
  // questo controller stesso non ha mai avuto una lettura riuscita, resta quello che gli è stato
  // fornito da initialSnapshot, quale che sia). `activityStale`/`activityContentUnknown`: stato
  // esatto al momento dell'interrogazione, cosicché un chiamante che sta per sostituire questo
  // controller possa seminare il successivo senza mai perdere un ritentativo pendente o
  // dichiarare erroneamente noto un contenuto che non lo è mai stato.
  function getActivityHandoff(){
    return {
      nominations: lastAppliedSnapshot.nominations,
      activityStale,
      activityContentUnknown
    };
  }

  return { stop, invalidateForMutation, getActivityHandoff };
}
